import { useAsyncFn } from 'react-use-v13';
import { AttemptReviewMachineEvent } from '@scale/shared/types/qaOperation/stateMachineDefs/attemptReviewStateMachine';
import { apiTransitionOperation } from 'react-scale/hooks/qaOperation/qaOperationApi';
import { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import { AsyncState } from 'react-use-v13/lib/useAsyncFn';
import React from 'react';
import { TransitionAttemptReviewOperationResponse } from '@scale/shared/types/qaOperation/api';
import { ReviewNodeOutcome } from '@scale/shared/types/qaOperation/qaOperationNode';

interface UseQaOperationStateMachineProps {
  userId: string;
}

export interface IQAOperationStateMachine {
  transitionOperationResult: AsyncState<TransitionAttemptReviewOperationResponse>;
  completeAttemptNode: (operationId: string, body?: any) => Promise<undefined>;
  retryTransition: (operationId: string) => Promise<undefined>;
  rejectNode: (operationId: string, message: string) => Promise<undefined>;
  approveNode: (operationId: string, message: string) => Promise<undefined>;
  transitionOperation: (
    operationId: string,
    event: AttemptReviewMachineEvent,
  ) => Promise<undefined>;
  cancelOperation: (operationId: string) => Promise<undefined>;
  claimAttemptNode: (operationId: string) => Promise<undefined>;
  claimReviewNode: (operationId: string) => Promise<undefined>;
}

/**
 * Hooks to help you transition QA Operation State Machines in a type-safe way
 * This may not scale as we have more and more types of state machines so break this up into
 * different hooks for each type of state machine in the future
 *
 * @param userId
 */
export const useQaOperationStateMachine = ({
  userId,
}: UseQaOperationStateMachineProps): IQAOperationStateMachine => {
  // @ts-ignore FIXME - Typescript 2345
  const [transitionOperationResult, transitionOperation] = useAsyncFn(
    // @ts-ignore FIXME - Typescript 2345
    async (operationId: string, event: AttemptReviewMachineEvent) => {
      try {
        return await apiTransitionOperation<AttemptReviewMachineEvent>(operationId, event);
      } catch (error) {
        defaultApiFetchErrorHandler(error, { handleHttp: true });
        throw error;
      }
    },
    [],
  ) as [
    AsyncState<TransitionAttemptReviewOperationResponse>,
    (operationId: string, event: AttemptReviewMachineEvent) => Promise<undefined>,
  ];

  const cancelOperation = React.useCallback(
    (operationId: string) =>
      transitionOperation(operationId, {
        type: 'cancel',
        userId,
      }),
    [transitionOperation, userId],
  );

  const claimAttemptNode = React.useCallback(
    (operationId: string) =>
      transitionOperation(operationId, {
        type: 'claimAttempt',
        userId,
      }),
    [transitionOperation, userId],
  );

  const claimReviewNode = React.useCallback(
    (operationId: string) =>
      transitionOperation(operationId, {
        type: 'claimReview',
        userId,
      }),
    [transitionOperation, userId],
  );

  const completeAttemptNode = React.useCallback(
    (operationId: string, body?: any) =>
      transitionOperation(operationId, {
        type: 'completeAttempt',
        userId,
        result: body ?? {},
      }),
    [transitionOperation, userId],
  );

  const completeReviewNode = React.useCallback(
    (operationId: string, outcome: ReviewNodeOutcome, message: string) =>
      transitionOperation(operationId, {
        type: 'completeReview',
        userId,
        result: { outcome, message },
      }),
    [transitionOperation, userId],
  );

  const retryTransition = React.useCallback(
    (operationId: string) =>
      transitionOperation(operationId, {
        type: 'retry',
      }),
    [transitionOperation],
  );

  const approveNode = React.useCallback(
    (operationId: string, message: string) =>
      completeReviewNode(operationId, ReviewNodeOutcome.Accept, message),
    [completeReviewNode],
  );

  const rejectNode = React.useCallback(
    (operationId: string, message: string) =>
      completeReviewNode(operationId, ReviewNodeOutcome.Reject, message),
    [completeReviewNode],
  );

  const qaOperationStateMachineHook = React.useMemo(
    () => ({
      transitionOperation,
      transitionOperationResult,
      cancelOperation,
      claimAttemptNode,
      claimReviewNode,
      completeAttemptNode,
      approveNode,
      rejectNode,
      retryTransition,
    }),
    [
      transitionOperation,
      transitionOperationResult,
      cancelOperation,
      claimAttemptNode,
      claimReviewNode,
      completeAttemptNode,
      approveNode,
      rejectNode,
      retryTransition,
    ],
  );

  return qaOperationStateMachineHook;
};
